import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../style.css';
import '../nov.css';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    { name: 'Physical Distribution', path: '/physical-distribution' },
    { name: 'Digital Distribution', path: '/services' },
    { name: 'Music Publishing', path: '/music-publishing' },
    { name: 'Artist Services', path: '/artist-services' },
    { name: 'Copyright Report', path: '/copyright-report' }
  ];

  const socialLinks = [
    {
      name: 'YouTube',
      url: 'https://www.youtube.com/@medianovofficial',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 transition duration-300 ease-in-out group-hover:fill-red-500">
          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2A29 29 0 0 0 23 11.75a29 29 0 0 0-.46-5.33z"></path>
          <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
        </svg>
      )
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/medianovofficial/',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 transition duration-300 ease-in-out group-hover:fill-pink-500">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
        </svg>
      )
    },
    {
      name: 'X (Twitter)',
      url: 'https://x.com/MediaNovRecords',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 transition duration-300 ease-in-out group-hover:fill-blue-400">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      )
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/company/media-nov',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 transition duration-300 ease-in-out group-hover:fill-blue-600">
          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
          <rect x="2" y="9" width="4" height="12"></rect>
          <circle cx="4" cy="4" r="2"></circle>
        </svg>
      )
    }
  ];

  return (
    <footer id="colophon" className="footer bg-gray-900 py-16 lg:py-24 relative overflow-hidden" style={{ fontFamily: 'aesthet-nova' }}>
      <div className="container mx-auto px-4 lg:px-0 relative z-10">
        
        {/* Logo Section */}
        <motion.div 
          className="flex flex-col items-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="mb-4"
            whileHover={{ rotate: 180 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" aria-label="Home">
              <img alt="Media Nov Logo" className="h-16 w-auto" src="/medinov.png" />
            </Link>
          </motion.div>
          <p className="text-white text-center text-sm uppercase">
            Empowering artists and labels to take control of their music.
          </p>
        </motion.div>

        {/* Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8 text-white">
          
          {/* Quick Links */}
          <motion.div 
            className="space-y-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold mb-4 uppercase">Quick Links</h4>
            <ul className="space-y-1">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-white transition duration-300 hover:underline uppercase text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div 
            className="space-y-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold mb-4 uppercase">Our Services</h4>
            <ul className="space-y-1">
              {services.map((service, index) => (
                <li key={index}>
                  {service.path.startsWith('#') ? (
                    <a 
                      href={service.path}
                      className="text-gray-400 hover:text-white transition duration-300 hover:underline uppercase text-sm"
                    >
                      {service.name}
                    </a>
                  ) : (
                    <Link 
                      to={service.path}
                      className="text-gray-400 hover:text-white transition duration-300 hover:underline uppercase text-sm"
                    >
                      {service.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div 
            className="space-y-2 md:text-right"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-lg font-semibold mb-4 uppercase">Contact Us</h4>
            <p className="text-gray-400 text-sm uppercase">
              <a href="mailto:contact@medianov.com" className="hover:text-white transition duration-300 hover:underline">
                contact@medianov.com
              </a>
            </p>
            <p className="text-gray-400 text-sm uppercase">
              <a href="tel:+12133295216" className="hover:text-white transition duration-300 hover:underline">
                US: +1 (213) 329-5216
              </a>
            </p>
            <p className="text-gray-400 text-sm uppercase">
              <a href="tel:+447438395244" className="hover:text-white transition duration-300 hover:underline">
                Global: +44 7438 395244
              </a>
            </p>
          </motion.div>

          {/* Social Media */}
          <motion.div 
            className="flex flex-col space-y-0 items-center md:items-start"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h4 className="text-lg font-semibold mb-1 uppercase text-center md:text-left">Follow Us</h4>
            <div className="flex space-x-1.5 justify-center md:justify-start">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition duration-300 group"
                  aria-label={social.name}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

        </div>

        {/* Copyright */}
        <motion.div 
          className="text-center text-gray-400 text-sm mt-8 uppercase"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          © 1996 - {new Date().getFullYear()} Media Nov. All rights reserved. Media Nov and the registered trademarks of Media Nov Inc. &amp; Media Nov Records Ltd.
        </motion.div>

      </div>
    </footer>
  );
};

export default Footer;
